import * as React from 'react';

import {Button} from 'shells/button/button';
import {InvoicesSection} from 'shells/lead-sidebar/sales-documents/invoices-section';
import {QuotesSection} from 'shells/lead-sidebar/sales-documents/quotes-section';
import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';
import {SectionAddItemButton} from 'shells/lead-sidebar/section-add-item-button';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {QuoteAndInvoiceInfoModal} from 'shells/modal/quote-and-invoice-info-modal';
import {SectionLabel} from 'shells/typography/section-label';

import type {QuoteDisplayStatus, SalesDocumentVariant} from 'nutshell-graphql-types';

import {useGetInstanceInfo} from '../../../core/use-get-instance-info';
import {EmailModalWrapper} from '../../../core-entity/components/email-modal-wrapper';

import {useGetInvoices} from '../../sales-documents/invoices/graphql/hooks';
import {useGetQuotes, useCreateInvoiceFromQuote} from '../../sales-documents/quotes/graphql/hooks';
import {
    downloadQuote,
    preventRefetchLoadingState,
    handleSalesDocumentChange,
} from '../../sales-documents/shared';
import {
    useCreateSalesDocument,
    useUpdateSalesDocumentStatus,
    useDuplicateSalesDocument,
} from '../../sales-documents/shared/graphql/hooks';

import styles from '../../sales-documents/shared/quotes.module.css';

const NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID = 'nutshell-quotes-container';

export function SidebarSalesDocumentsSection() {
    const [isInfoModalOpen, setIsInfoModalOpen] = React.useState<boolean>(false);
    const [emailModalType, setEmailModalType] = React.useState<SalesDocumentType | null>(null);
    const refetchLoading = React.useRef<boolean>(false);

    // @ts-expect-error
    const leadId = window.Lead.getApiId();

    const {sessionInstance} = useGetInstanceInfo();
    const scriptRef = React.useRef<HTMLElement | null | undefined>(null);

    const {createSalesDocument} = useCreateSalesDocument();
    const {isLoading, quotes, refetch} = useGetQuotes(leadId, !leadId);
    const {
        isLoading: isLoadingInvoices,
        invoices,
        refetch: refetchInvoices,
    } = useGetInvoices(leadId, !leadId);

    const {updateSalesDocumentStatus} = useUpdateSalesDocumentStatus();
    const {duplicateSalesDocument} = useDuplicateSalesDocument();
    const {createInvoiceFromQuote} = useCreateInvoiceFromQuote();

    const instanceId = sessionInstance && sessionInstance.instanceId;

    const handleSendQuoteViaEmail = React.useCallback(() => {
        setEmailModalType('Quote');
    }, []);

    const handleSendInvoiceViaEmail = React.useCallback(() => {
        setEmailModalType('Invoice');
    }, []);

    const handlePostEmailModalOpen = () => {
        setEmailModalType(null);
    };

    const handleCreate = (salesDocumentVariant: SalesDocumentVariant) => {
        createSalesDocument({
            leadId: leadId,
            salesDocumentVariant: salesDocumentVariant,
        }).then((response) => {
            const salesDocumentRes = response && response.data && response.data.salesDocumentCreate;
            if (salesDocumentRes) {
                handleSalesDocumentChange(
                    salesDocumentRes.salesDocument.modalUrl,
                    refetchLoading,
                    salesDocumentVariant === 'QUOTE' ? refetch : refetchInvoices
                );
            }
        });
    };

    const handleDownload = (slug: string) => {
        if (!instanceId || !scriptRef.current) {
            return;
        }

        downloadQuote(instanceId, slug, scriptRef.current, NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID);
    };

    const handleUpdateStatus = (id: string, status: QuoteDisplayStatus) => {
        updateSalesDocumentStatus({
            id: id,
            status,
        }).then((response) => {
            Timeline.refreshTimeline();

            const salesDocumentRes =
                response && response.data && response.data.salesDocumentUpdateStatus;

            if (salesDocumentRes && salesDocumentRes.salesDocument) {
                preventRefetchLoadingState(
                    refetchLoading,
                    salesDocumentRes.salesDocument.__typename === 'Quote'
                        ? refetch
                        : refetchInvoices
                );
            }
        });
    };

    const handleDuplicateSalesDocument = (id: string) => {
        duplicateSalesDocument({
            salesDocument: id,
        }).then((response) => {
            const salesDocumentRes =
                response && response.data && response.data.salesDocumentDuplicate;
            if (salesDocumentRes && salesDocumentRes.salesDocument) {
                handleSalesDocumentChange(
                    salesDocumentRes.salesDocument.modalUrl,
                    refetchLoading,
                    salesDocumentRes.salesDocument.__typename === 'Quote'
                        ? refetch
                        : refetchInvoices
                );
            }
        });
    };

    const handleCreateInvoiceFromQuote = (id: string) => {
        createInvoiceFromQuote({
            quote: id,
        }).then((response) => {
            const invoiceRes = response && response.data && response.data.invoiceCreateFromQuote;
            if (invoiceRes && invoiceRes.invoice) {
                handleSalesDocumentChange(
                    invoiceRes.invoice.modalUrl,
                    refetchLoading,
                    refetchInvoices
                );
            }
        });
    };

    const sectionTitle = 'Quotes & Invoices';

    return (
        <>
            <div className={styles.section}>
                <SectionLabel>
                    {sectionTitle}
                    <div className={styles['learn-more-container']}>
                        <Button onClick={() => setIsInfoModalOpen(true)} variant='text-primary'>
                            Learn more
                        </Button>
                    </div>
                </SectionLabel>

                {(isLoading || isLoadingInvoices || !invoices || !quotes) &&
                !refetchLoading.current ? (
                    <LoadingPage />
                ) : (
                    <div className='flex flex-dir-col gap-8'>
                        <InvoicesSection
                            invoices={invoices}
                            handleDownloadInvoice={handleDownload}
                            handleUpdateInvoiceStatus={handleUpdateStatus}
                            handleDuplicateInvoice={handleDuplicateSalesDocument}
                            handleSendInvoiceViaEmail={handleSendInvoiceViaEmail}
                        />
                        <QuotesSection
                            quotes={quotes}
                            handleDownloadQuote={handleDownload}
                            handleUpdateQuoteStatus={handleUpdateStatus}
                            handleDuplicateQuote={handleDuplicateSalesDocument}
                            handleCreateInvoiceFromQuote={handleCreateInvoiceFromQuote}
                            handleSendQuoteViaEmail={handleSendQuoteViaEmail}
                        />
                    </div>
                )}
                <div className='mt-16'>
                    <div className='flex gap-8'>
                        <SectionAddItemButton
                            sectionEntity='quotes'
                            isAddingItem={false}
                            setIsAddingItem={() => handleCreate('QUOTE')}
                            parentItemText=' a quote'
                            omitEntityName={true}
                        />
                        <SectionAddItemButton
                            sectionEntity='quotes'
                            isAddingItem={false}
                            setIsAddingItem={() => handleCreate('INVOICE')}
                            parentItemText=' an invoice'
                            omitEntityName={true}
                        />
                    </div>
                </div>
                <QuoteAndInvoiceInfoModal
                    isOpen={isInfoModalOpen}
                    onClose={() => setIsInfoModalOpen(false)}
                />
                {emailModalType && leadId && (
                    <EmailModalWrapper
                        entityId={leadId}
                        postModalOpen={handlePostEmailModalOpen}
                        originSalesDocumentType={emailModalType}
                    />
                )}
            </div>
            {/* @ts-expect-error */}
            <div id={NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID} ref={scriptRef} />
        </>
    );
}
